import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { BookingEngineService } from '../../api';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Stack,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Tooltip,
  Badge,
  InputAdornment,
  MenuItem,
  Select,
  InputLabel,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import {
  CalendarToday,
  Person,
  CheckCircle,
  Close,
  LocalOffer,
  KingBed,
  Wifi,
  Landscape,
  FreeBreakfast,
  Coffee,
  Bathtub,
  InfoOutlined,
  CompareArrows,
  DeleteOutline,
  Done,
  Security,
  Print,
  PictureAsPdf,
  RoomService,
  AcUnit,
  FilterList,
  Phone,
  LocationOn,
  Stars,
  Star,
  Search,
  Remove,
  Add,
  Menu as MenuIcon
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getTourismDirhamRate, isUaeProperty } from '../../utils/dubaiHospitalityTax';

// Currency code → symbol & locale mapping
const CURRENCY_MAP = {
  INR: { symbol: '₹', prefix: 'Rs', locale: 'en-IN' },
  AED: { symbol: 'د.إ', prefix: 'AED', locale: 'en-AE' },
  USD: { symbol: '$', prefix: '$', locale: 'en-US' },
  EUR: { symbol: '€', prefix: '€', locale: 'de-DE' },
  GBP: { symbol: '£', prefix: '£', locale: 'en-GB' },
  SAR: { symbol: '﷼', prefix: 'SAR', locale: 'ar-SA' },
  QAR: { symbol: 'ر.ق', prefix: 'QAR', locale: 'ar-QA' },
  BHD: { symbol: 'BD', prefix: 'BHD', locale: 'ar-BH' },
  OMR: { symbol: 'ر.ع.', prefix: 'OMR', locale: 'ar-OM' },
  KWD: { symbol: 'د.ك', prefix: 'KWD', locale: 'ar-KW' }
};

const getCurrencyInfo = (code) => {
  const upper = (code || 'INR').toUpperCase();
  return CURRENCY_MAP[upper] || { symbol: upper, prefix: upper, locale: 'en-US' };
};

// Theme with exact resort green palette from the screenshot
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#008744', // PMSGO resort green
      dark: '#006633',
      light: '#2bb673',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f59e0b',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#475569',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
});

// Room data with rich details matching the screenshot and resort inventory
// Room data with rich details matching the screenshot and resort inventory
const INITIAL_ROOMS = [
  {
    id: 'geo-dome-stay',
    title: 'Geodesic Dome Stay',
    category: 'Geodesic Dome',
    capacity: 3,
    adultsBase: 2,
    childrenBase: 1,
    roomsLeft: 2,
    basePrice: 11210,
    image: 'https://images.unsplash.com/photo-1587061949409-02df41d5e562?auto=format&fit=crop&w=800&q=80',
    description: 'Settled in the cloudy hills of Vagamon, where the serenity of nature shines in its fallout. Elappara is the perfect spot to chill and enjoy the thrill of scenic Kerala.',
    amenities: ['Panoramic Valley View', 'King Bed', 'Private Wooden Deck', 'Free Wi-Fi', 'En-suite Glass Bath', 'Electric Kettle', 'Hill Station Heating'],
    highlights: ['Best Seller', 'Hurry! 2 Rooms Left'],
    bedType: '1 King Bed + 1 Day Bed',
    size: '480 sq.ft'
  },
  {
    id: 'luxury-cottage-stay',
    title: 'Luxury Valley View Cottage',
    category: 'Luxury Cottage',
    capacity: 4,
    adultsBase: 2,
    childrenBase: 2,
    roomsLeft: 3,
    basePrice: 9450,
    image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=800&q=80',
    description: 'Rustic Kerala architectural heritage blended with contemporary luxury. Features an expansive open balcony directly facing the cascading green hills of Vagamon.',
    amenities: ['Private Balcony', 'Rainforest Shower', 'Smart TV', 'Free Wi-Fi', '24/7 Hot Water', 'Mountain Spring Water'],
    highlights: ['Spacious Family Suite', '3 Rooms Left'],
    bedType: '1 King Bed + 2 Twin Beds',
    size: '550 sq.ft'
  },
  {
    id: 'treehouse-sanctuary',
    title: 'Canopy Treehouse Villa',
    category: 'Treehouse Villa',
    capacity: 2,
    adultsBase: 2,
    childrenBase: 0,
    roomsLeft: 1,
    basePrice: 15900,
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=800&q=80',
    description: 'Perched high in the canopy among pine branches with 360-degree mist views. Complete privacy tailored for honeymooners and romantic mountain getaways.',
    amenities: ['Candlelight Dinner Setup', '360° Mist View Deck', 'Jacuzzi Tub', 'Private Butler Service', 'Wi-Fi'],
    highlights: ['Only 1 Villa Left!', 'Honeymoon Special'],
    bedType: '1 Emperor Bed',
    size: '620 sq.ft'
  }
];

export default function GuestBookingEngine() {
  const params = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const queryTenantId = searchParams.get('tenantId') || searchParams.get('id') || searchParams.get('hotelId');
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const rawId = params.id || params['*']?.replace(/^\//, '');
  const urlTenantId = queryTenantId || ((rawId && rawId !== 'booking' && rawId !== 'book') ? rawId : (pathSegments.length > 1 && pathSegments[0] === 'booking' ? pathSegments[1] : undefined));

  
  const getTodayFormatted = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getTomorrowFormatted = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Dates state (dynamic today as checkIn and tomorrow as checkOut)
  const [checkIn, setCheckIn] = useState(getTodayFormatted);
  const [checkOut, setCheckOut] = useState(getTomorrowFormatted);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [roomsCount, setRoomsCount] = useState(1);

  // Dynamic rooms list & resort profile state
  const [roomsList, setRoomsList] = useState([]);
  const [resortProfile, setResortProfile] = useState({
    name: '',
    tagline: '',
    currency: 'INR'
  });
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  const [hotelNotFound, setHotelNotFound] = useState(false);
  const [hotelErrorMessage, setHotelErrorMessage] = useState('');

  // Dynamic currency derived from resort profile
  const currencyInfo = useMemo(() => getCurrencyInfo(resortProfile.currency), [resortProfile.currency]);
  const currencySymbol = currencyInfo.prefix;
  const currencyLocale = currencyInfo.locale;
  const currencyCode = (resortProfile.currency || 'INR').toUpperCase();

  // Guest details form state (declared before enquiry and checkout handlers)
  const [guestDetails, setGuestDetails] = useState({
    title: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    country: (resortProfile?.currency === 'AED' || resortProfile?.country === 'UAE') ? 'United Arab Emirates' : 'India',
    eta: '14:00',
    specialRequests: '',
    paymentMethod: 'payAtHotel', // 'payAtHotel' or 'online'
    termsAccepted: true
  });

  // Search & display options
  const [priceMode, setPriceMode] = useState('perNight'); // 'perNight' or 'wholeStay'
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discountPercent }
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('ALL');

  // Selected rooms in cart: { [roomId]: quantity }
  const [selectedRooms, setSelectedRooms] = useState({});

  // Room comparison
  const [compareList, setCompareList] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  // Room info modal
  const [activeInfoRoom, setActiveInfoRoom] = useState(null);

  // Room enquiry modal & lead submission state
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [enquiryRoom, setEnquiryRoom] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    message: ''
  });
  const [isSubmittingEnquiry, setIsSubmittingEnquiry] = useState(false);
  const [enquiryResult, setEnquiryResult] = useState(null);

  const handleOpenEnquiry = (room) => {
    setEnquiryRoom(room);
    setEnquiryForm({
      name: guestDetails.firstName ? `${guestDetails.firstName} ${guestDetails.lastName}`.trim() : '',
      email: guestDetails.email || '',
      phone: guestDetails.phone || '',
      checkIn: checkIn,
      checkOut: checkOut,
      message: `Hi, I would like to inquire about availability and rates for ${room?.title || 'this room'}.`
    });
    setEnquiryResult(null);
    setEnquiryModalOpen(true);
  };

  const handleCloseEnquiry = () => {
    setEnquiryModalOpen(false);
    setEnquiryRoom(null);
    setEnquiryResult(null);
  };

  const handleSubmitEnquiry = async () => {
    if (!enquiryForm.name || !enquiryForm.email || !enquiryForm.phone || !enquiryForm.message) {
      setToast({
        open: true,
        message: 'Please fill in all required fields (Name, Email, Phone, and Message).',
        severity: 'warning'
      });
      return;
    }

    setIsSubmittingEnquiry(true);
    try {
      const payload = {
        name: enquiryForm.name,
        email: enquiryForm.email,
        phone: enquiryForm.phone,
        roomTypeId: enquiryRoom?.id || undefined,
        checkIn: enquiryForm.checkIn || checkIn,
        checkOut: enquiryForm.checkOut || checkOut,
        message: enquiryForm.message,
        tenantId: urlTenantId
      };

      const res = await BookingEngineService.submitEnquiry(payload);
      if (res?.status === 'success' || res?.data) {
        setEnquiryResult(res.data || res);
        setToast({
          open: true,
          message: 'Enquiry submitted successfully!',
          severity: 'success'
        });
      } else {
        throw new Error(res?.message || 'Failed to submit enquiry');
      }
    } catch (err) {
      console.error('Submit enquiry error:', err);
      setToast({
        open: true,
        message: err.response?.data?.message || err.message || 'Error submitting enquiry. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmittingEnquiry(false);
    }
  };

  // Checkout modal & flow
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedReservation, setConfirmedReservation] = useState(null);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [testRazorpayModal, setTestRazorpayModal] = useState({ open: false, orderData: null, bookedRoomsData: null });

  // Cookies banner
  const [cookieConsent, setCookieConsent] = useState(true);

  // Toast notifications
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  // Calculate nights
  const numberOfNights = useMemo(() => {
    try {
      const d1 = new Date(checkIn);
      const d2 = new Date(checkOut);
      const diffTime = d2.getTime() - d1.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 1;
    } catch {
      return 1;
    }
  }, [checkIn, checkOut]);

  // Fetch live availability & resort profile from Backend API
  const fetchLiveAvailability = useCallback(async () => {
    if (!urlTenantId) {
      setHotelNotFound(true);
      setHotelErrorMessage('');
      setIsLoadingRooms(false);
      return;
    }

    try {
      setIsLoadingRooms(true);
      setHotelNotFound(false);
      setHotelErrorMessage('');
      
      // 1. Fetch Resort Profile
      const profileRes = await BookingEngineService.getHotelProfile(urlTenantId);
      if (profileRes?.data) {
        setResortProfile(profileRes.data);
      }

      // 2. Search Live Room Availability & Pricing
      const searchRes = await BookingEngineService.searchAvailability({
        tenantId: urlTenantId,
        checkIn,
        checkOut,
        adults,
        kids: children
      });

      const hotelIsUae = (profileRes?.data?.currency === 'AED') || isUaeProperty(profileRes?.data);

      if (searchRes?.data?.roomTypes) {
        const transformedRooms = searchRes.data.roomTypes.map((rt) => {
          const nightRate = rt.pricing?.perNightRate || rt.baseRate || 1000;
          const totalRate = rt.pricing?.totalStayRate || (nightRate * numberOfNights);

          const defaultGstPct = nightRate > 7500 ? 18 : (nightRate > 1000 ? 12 : 0);
          const taxObj = rt.pricing?.tax || {
            gstPct: hotelIsUae ? 0 : defaultGstPct,
            cgstPct: hotelIsUae ? 0 : defaultGstPct / 2,
            sgstPct: hotelIsUae ? 0 : defaultGstPct / 2,
            perNightTax: hotelIsUae ? 0 : Math.round((nightRate * defaultGstPct) / 100),
            totalStayTax: hotelIsUae ? 0 : Math.round((nightRate * defaultGstPct * numberOfNights) / 100),
            taxSlabLabel: hotelIsUae ? 'UAE Taxes & Fees' : `${defaultGstPct}% GST`
          };

          return {
            tax: taxObj,
            id: rt.id,
            roomTypeId: rt.id,
            title: rt.name,
            category: rt.name,
            capacity: (rt.maxAdults ?? 2) + (rt.maxKids ?? 0),
            adultsBase: rt.maxAdults ?? 2,
            childrenBase: rt.maxKids ?? 0,
            roomsLeft: rt.availableCount ?? 2,
            basePrice: nightRate,
            perNightRate: nightRate,
            totalStayRate: totalRate,
            image: (rt.images && rt.images.length > 0) ? rt.images[0] : 'https://images.unsplash.com/photo-1587061949409-02df41d5e562?auto=format&fit=crop&w=800&q=80',
            images: (rt.images && rt.images.length > 0) ? rt.images : ['https://images.unsplash.com/photo-1587061949409-02df41d5e562?auto=format&fit=crop&w=800&q=80'],
            description: rt.description || '',
            amenities: rt.defaultAmenities || [],
            highlights: rt.urgencyBadge ? [rt.urgencyBadge] : (rt.availableCount === 0 ? ['Sold Out'] : (rt.availableCount > 0 && rt.availableCount <= 3 ? [`Hurry! ${rt.availableCount} Room${rt.availableCount > 1 ? 's' : ''} Left`] : [])),
            bedType: rt.bedType || '1 King Bed',
            size: rt.size || rt.roomSize || '480 sq.ft'
          };
        });
        setRoomsList(transformedRooms);
      }
    } catch (err) {
      console.warn('Backend API error:', err);
      const responseMsg = err?.response?.data?.message || err?.message || '';
      setHotelErrorMessage(responseMsg);
      setHotelNotFound(true);
    } finally {
      setIsLoadingRooms(false);
    }
  }, [urlTenantId, checkIn, checkOut, adults, children, numberOfNights]);


  // Trigger availability API on initial page mount only (manual "Check Availability" button handles subsequent calls)
  useEffect(() => {
    fetchLiveAvailability();
  }, [urlTenantId]);

  // Update document title and dynamic metadata when resort profile loads
  useEffect(() => {
    if (resortProfile?.name) {
      document.title = `${resortProfile.name} | Official Direct Booking`;

      const updateMetaTag = (selector, attr, val) => {
        const el = document.querySelector(selector);
        if (el) el.setAttribute(attr, val);
      };

      const desc = `Book your stay directly at ${resortProfile.name}${resortProfile.city ? ' in ' + resortProfile.city : ''}. Best rates guaranteed and instant confirmation.`;
      updateMetaTag('meta[name="description"]', 'content', desc);
      updateMetaTag('meta[property="og:title"]', 'content', `${resortProfile.name} | Official Direct Booking`);
      updateMetaTag('meta[property="og:description"]', 'content', desc);
      updateMetaTag('meta[property="og:url"]', 'content', window.location.href);
      updateMetaTag('link[rel="canonical"]', 'href', window.location.href);
      updateMetaTag('meta[name="twitter:title"]', 'content', `${resortProfile.name} | Official Direct Booking`);
      updateMetaTag('meta[name="twitter:description"]', 'content', desc);
    }
  }, [resortProfile]);

  const handleCloseCheckout = () => {
    if (isSubmittingBooking) return;
    setCheckoutModalOpen(false);
    // Reset guest form so next booking starts clean
    setGuestDetails({
      title: 'Mr',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      city: '',
      country: 'India',
      eta: '14:00',
      specialRequests: '',
      paymentMethod: 'payAtHotel',
      termsAccepted: true
    });
    if (bookingConfirmed) {
      setBookingConfirmed(false);
      setConfirmedReservation(null);
      setSelectedRooms({});
      setAppliedPromo(null);
      fetchLiveAvailability();
    }
  };


  // Total physical room inventory count across all room types
  const totalPhysicalRoomsCount = useMemo(() => {
    return roomsList.reduce((acc, r) => acc + (r.roomsLeft ?? 1), 0);
  }, [roomsList]);

  // Available dynamic categories from rooms list
  const availableCategories = useMemo(() => {
    const cats = new Set();
    roomsList.forEach(room => {
      if (room.category) {
        cats.add(room.category);
      }
    });
    return Array.from(cats);
  }, [roomsList]);

  // Filtered rooms
  const filteredRooms = useMemo(() => {
    return roomsList.filter(room => {
      if (filterCategory !== 'ALL' && room.category !== filterCategory) return false;
      return true;
    });
  }, [roomsList, filterCategory]);

  // Cart calculations
  const totalSelectedRoomsCount = useMemo(() => {
    return Object.values(selectedRooms).reduce((acc, qty) => acc + qty, 0);
  }, [selectedRooms]);

  const pricingBreakdown = useMemo(() => {
    const isUae = (resortProfile?.currency === 'AED') || isUaeProperty(resortProfile);
    let subtotalPerNight = 0;
    let roomRateTotal = 0;
    let totalTaxAmount = 0;
    const gstPctSet = new Set();

    let totalServiceCharge = 0;
    let totalMunicipalityFee = 0;
    let totalVat = 0;
    let totalTourismDirham = 0;

    Object.entries(selectedRooms).forEach(([roomId, qty]) => {
      const r = roomsList.find(x => x.id === roomId);
      if (r && qty > 0) {
        const roomStayCost = r.basePrice * qty * numberOfNights;
        subtotalPerNight += r.basePrice * qty;
        roomRateTotal += roomStayCost;

        if (isUae) {
          if (r.tax?.vatAmount !== undefined || r.tax?.tourismDirhamTotal !== undefined) {
            totalServiceCharge += (r.tax.serviceChargeAmount || 0) * qty;
            totalMunicipalityFee += (r.tax.municipalityFeeAmount || 0) * qty;
            totalVat += (r.tax.vatAmount || 0) * qty;
            totalTourismDirham += (r.tax.tourismDirhamTotal ?? r.tax.tourismDirhamAmount ?? 0) * qty;
            totalTaxAmount += (r.tax.totalStayTax || 0) * qty;
          } else {
            const tdRate = getTourismDirhamRate(resortProfile?.rating, resortProfile?.hotelType || r.category);
            const billableNights = Math.min(numberOfNights, 30);
            const tdAmount = tdRate * billableNights * qty;
            const scAmount = Number(((roomStayCost * 10) / 100).toFixed(2));
            const munAmount = Number(((roomStayCost * 7) / 100).toFixed(2));
            const vatBase = roomStayCost + scAmount + munAmount;
            const vat = Number(((vatBase * 5) / 100).toFixed(2));

            totalServiceCharge += scAmount;
            totalMunicipalityFee += munAmount;
            totalVat += vat;
            totalTourismDirham += tdAmount;
            totalTaxAmount += (scAmount + munAmount + vat + tdAmount);
          }
        } else {
          const gstPct = r.tax?.gstPct ?? (r.basePrice > 7500 ? 18 : (r.basePrice > 1000 ? 12 : 0));
          gstPctSet.add(gstPct);

          const roomTaxPerNight = r.tax?.perNightTax ?? ((r.basePrice * gstPct) / 100);
          totalTaxAmount += roomTaxPerNight * qty * numberOfNights;
        }
      }
    });

    let discount = 0;
    if (appliedPromo) {
      discount = Number(((roomRateTotal * appliedPromo.discountPercent) / 100).toFixed(2));
    }
    const netTaxableValue = Math.max(0, roomRateTotal - discount);

    let finalTaxAmount = totalTaxAmount;
    if (discount > 0 && roomRateTotal > 0) {
      const discountRatio = netTaxableValue / roomRateTotal;
      if (isUae) {
        totalServiceCharge = Number((totalServiceCharge * discountRatio).toFixed(2));
        totalMunicipalityFee = Number((totalMunicipalityFee * discountRatio).toFixed(2));
        const discountedVatBase = netTaxableValue + totalServiceCharge + totalMunicipalityFee;
        totalVat = Number(((discountedVatBase * 5) / 100).toFixed(2));
        finalTaxAmount = Number((totalServiceCharge + totalMunicipalityFee + totalVat + totalTourismDirham).toFixed(2));
      } else {
        finalTaxAmount = Number((totalTaxAmount * discountRatio).toFixed(2));
      }
    }

    const finalTotal = Number((netTaxableValue + finalTaxAmount).toFixed(2));

    let taxLabel = 'GST';
    if (isUae) {
      taxLabel = 'UAE VAT & Dubai Fees';
    } else if (gstPctSet.size === 1) {
      const singlePct = Array.from(gstPctSet)[0];
      taxLabel = `${singlePct}% GST`;
    } else if (gstPctSet.size > 1) {
      const sortedPcts = Array.from(gstPctSet).sort((a, b) => a - b);
      taxLabel = `${sortedPcts.join('% / ')}% GST`;
    }

    return {
      subtotalPerNight,
      baseForStay: roomRateTotal,
      discount,
      netTaxableValue,
      totalTaxAmount: finalTaxAmount,
      taxLabel,
      finalTotal,
      isUae,
      serviceCharge: totalServiceCharge,
      municipalityFee: totalMunicipalityFee,
      vat: totalVat,
      tourismDirham: totalTourismDirham
    };
  }, [roomsList, selectedRooms, numberOfNights, appliedPromo, resortProfile]);

  // Handlers
  const handleAddRoom = (roomId) => {
    const targetRoom = roomsList.find(r => r.id === roomId);
    if (targetRoom && targetRoom.roomsLeft <= 0) {
      setToast({
        open: true,
        message: 'Sorry, this room type is sold out for the selected dates!',
        severity: 'warning'
      });
      return;
    }

    setSelectedRooms(prev => ({
      ...prev,
      [roomId]: (prev[roomId] || 0) + 1
    }));
    setToast({
      open: true,
      message: 'Room added to booking summary!',
      severity: 'success'
    });
  };

  const handleRemoveRoom = (roomId) => {
    setSelectedRooms(prev => {
      const copy = { ...prev };
      delete copy[roomId];
      return copy;
    });
  };

  const handleUpdateQuantity = (roomId, delta) => {
    const targetRoom = roomsList.find(r => r.id === roomId);
    setSelectedRooms(prev => {
      const current = prev[roomId] || 0;
      const next = current + delta;
      if (delta > 0 && targetRoom && next > targetRoom.roomsLeft) {
        setToast({
          open: true,
          message: `Only ${targetRoom.roomsLeft} room(s) available for this type!`,
          severity: 'warning'
        });
        return prev;
      }
      if (next <= 0) {
        const copy = { ...prev };
        delete copy[roomId];
        return copy;
      }
      return { ...prev, [roomId]: next };
    });
  };

  const handleToggleCompare = (roomId) => {
    setCompareList(prev => {
      if (prev.includes(roomId)) {
        return prev.filter(id => id !== roomId);
      } else {
        if (prev.length >= 3) {
          setToast({
            open: true,
            message: 'You can compare up to 3 rooms at a time',
            severity: 'warning'
          });
          return prev;
        }
        return [...prev, roomId];
      }
    });
  };

  const handleApplyPromoCode = async () => {
    const rawInput = promoCodeInput.trim().toUpperCase();
    if (!rawInput) return;

    // Check if input is a numeric discount percentage (e.g. "10%", "15", "20%")
    const cleanNum = rawInput.replace('%', '');
    const isNumber = !isNaN(Number(cleanNum)) && Number(cleanNum) > 0 && Number(cleanNum) <= 100;

    if (isNumber) {
      const discountPct = Math.min(100, Math.max(1, Number(cleanNum)));
      setAppliedPromo({ code: `${discountPct}% DISCOUNT`, discountPercent: discountPct });
      setToast({ open: true, message: `${discountPct}% discount applied successfully!`, severity: 'success' });
      setPromoDialogOpen(false);
      return;
    }

    try {
      const res = await BookingEngineService.validatePromoCode({
        code: rawInput,
        checkIn,
        checkOut,
        totalAmount: pricingBreakdown.baseForStay
      });
      if (res?.status === 'success' && res.data?.valid) {
        setAppliedPromo({ code: rawInput, discountPercent: res.data.discountPercent || 10 });
        setToast({ open: true, message: `Promo code ${rawInput} applied! ${res.data.discountPercent}% discount granted.`, severity: 'success' });
        setPromoDialogOpen(false);
      } else {
        let matchedPct = 10;
        if (rawInput.includes('20')) matchedPct = 20;
        else if (rawInput.includes('15')) matchedPct = 15;
        else if (rawInput.includes('5')) matchedPct = 5;

        setAppliedPromo({ code: rawInput, discountPercent: matchedPct });
        setToast({ open: true, message: `Promo code ${rawInput} applied! ${matchedPct}% discount granted.`, severity: 'success' });
        setPromoDialogOpen(false);
      }
    } catch (err) {
      let matchedPct = 10;
      if (rawInput.includes('20')) matchedPct = 20;
      else if (rawInput.includes('15')) matchedPct = 15;
      else if (rawInput.includes('5')) matchedPct = 5;

      setAppliedPromo({ code: rawInput, discountPercent: matchedPct });
      setToast({ open: true, message: `Discount code ${rawInput} applied! ${matchedPct}% discount granted.`, severity: 'success' });
      setPromoDialogOpen(false);
    }
  };

  // Checkout submission
  const handleSubmitBooking = async () => {
    if (!guestDetails.firstName || !guestDetails.email || !guestDetails.phone) {
      setToast({
        open: true,
        message: 'Please fill in all mandatory guest contact fields (*)',
        severity: 'error'
      });
      return;
    }

    setIsSubmittingBooking(true);

    try {
      const selectedRoomsList = Object.entries(selectedRooms)
        .filter(([_, qty]) => qty > 0)
        .map(([roomId, qty]) => {
          const roomObj = roomsList.find(r => r.id === roomId);
          return {
            roomTypeId: roomObj?.roomTypeId || roomId,
            quantity: qty
          };
        });

      const firstRoomObj = roomsList.find(r => r.id === (selectedRoomsList[0]?.roomTypeId || Object.keys(selectedRooms)[0]));

      const payload = {
        tenantId: urlTenantId,
        title: guestDetails.title,
        firstName: guestDetails.firstName,
        lastName: guestDetails.lastName,
        name: `${guestDetails.firstName} ${guestDetails.lastName}`.trim(),
        email: guestDetails.email,
        phone: guestDetails.phone,
        city: guestDetails.city,
        eta: guestDetails.eta,
        specialRequests: guestDetails.specialRequests,
        checkIn,
        checkOut,
        roomTypeId: firstRoomObj?.roomTypeId,
        rooms: selectedRoomsList,
        adults,
        kids: children,
        promoCode: appliedPromo?.code,
        paymentMethod: guestDetails.paymentMethod === 'payAtHotel' ? 'Pay at Resort' : 'Pay Online'
      };

      const res = await BookingEngineService.createReservation(payload);
      if (res?.status === 'success' && res.data) {
        if (res.data.razorpayPayload) {
          const { orderId, amount, currency, keyId } = res.data.razorpayPayload;
          const bookedRoomsData = Object.entries(selectedRooms).map(([id, qty]) => {
            const room = roomsList.find(r => r.id === id);
            return { ...room, quantity: qty };
          });

          const isDummyTestKey = !keyId || keyId === 'rzp_test_pmsgo' || (typeof keyId === 'string' && keyId.includes('pmsgo')) || (typeof orderId === 'string' && orderId.startsWith('order_dummy_'));

          if (isDummyTestKey) {
            setTestRazorpayModal({
              open: true,
              orderData: {
                orderId,
                amount: res.data.razorpayPayload.amountFormatted || (amount / 100).toFixed(2),
                currency: currency || 'INR',
                bookingReference: res.data.bookingReference
              },
              bookedRoomsData
            });
            return;
          }

          const loadScript = () => new Promise((resolve) => {
            if (window.Razorpay) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });

          const isScriptLoaded = await loadScript();
          if (!isScriptLoaded) {
            setToast({ open: true, message: 'Failed to load Razorpay SDK. Please check your network connection.', severity: 'error' });
            return;
          }

          const options = {
            key: keyId,
            amount: amount,
            currency: currency || 'INR',
            name: resortProfile?.name || 'PMSGO Resort',
            description: `Reservation Payment - ${res.data.bookingReference}`,
            order_id: orderId,
            prefill: {
              name: `${guestDetails.firstName} ${guestDetails.lastName}`.trim(),
              email: guestDetails.email,
              contact: guestDetails.phone
            },
            handler: async function (response) {
              try {
                const verifyRes = await BookingEngineService.verifyRazorpayPayment({
                  tenantId: urlTenantId,
                  bookingReference: res.data.bookingReference,
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature
                });

                if (verifyRes?.status === 'success') {
                  const confirmedData = {
                    bookingId: res.data.bookingReference,
                    guest: guestDetails,
                    rooms: bookedRoomsData,
                    checkIn,
                    checkOut,
                    nights: numberOfNights,
                    pricing: pricingBreakdown,
                    paymentStatus: 'Paid Online (Guaranteed)',
                    createdAt: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  };
                  setConfirmedReservation(confirmedData);
                  setBookingConfirmed(true);
                  fetchLiveAvailability();
                } else {
                  setToast({ open: true, message: 'Payment verification failed.', severity: 'error' });
                }
              } catch (err) {
                console.error('Razorpay verification error:', err);
                setToast({ open: true, message: 'Error verifying Razorpay payment.', severity: 'error' });
              }
            },
            theme: { color: resortProfile?.primaryColor || '#008744' }
          };

          try {
            const rzp = new window.Razorpay(options);
            rzp.open();
          } catch (rzpErr) {
            console.error('Razorpay SDK init error:', rzpErr);
            setTestRazorpayModal({
              open: true,
              orderData: {
                orderId,
                amount: res.data.razorpayPayload.amountFormatted || (amount / 100).toFixed(2),
                currency: currency || 'INR',
                bookingReference: res.data.bookingReference
              },
              bookedRoomsData
            });
          }
          return;
        }

        if (guestDetails.paymentMethod === 'online' && !res.data.razorpayPayload) {
          setToast({ open: true, message: 'Unable to initiate online Razorpay payment. Please try again or choose Pay at Resort.', severity: 'error' });
          return;
        }

        const bookedRoomsData = Object.entries(selectedRooms).map(([id, qty]) => {
          const room = roomsList.find(r => r.id === id);
          return { ...room, quantity: qty };
        });

        const confirmedData = {
          bookingId: res.data.bookingReference || `WM-2026-${Math.floor(1000 + Math.random() * 9000)}`,
          guest: guestDetails,
          rooms: bookedRoomsData,
          checkIn,
          checkOut,
          nights: numberOfNights,
          pricing: pricingBreakdown,
          paymentStatus: guestDetails.paymentMethod === 'payAtHotel' ? 'Pay at Check-In' : 'Paid Online (Guaranteed)',
          createdAt: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        };

        setConfirmedReservation(confirmedData);
        setBookingConfirmed(true);
        fetchLiveAvailability();
      }
    } catch (error) {
      console.error('Error submitting booking to API:', error);
      setToast({
        open: true,
        message: error?.response?.data?.message || error?.message || 'Unable to process reservation. Please check your details and try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Generate Voucher PDF with jsPDF
  const handleDownloadVoucherPdf = () => {
    if (!confirmedReservation) return;

    try {
      const doc = new jsPDF();
      doc.setFont('helvetica');

      // Header Banner
      doc.setFillColor(0, 135, 68);
      doc.rect(0, 0, 210, 35, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text((resortProfile.name || 'PMSGO RESORTS').toUpperCase(), 14, 18);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const locationText = [resortProfile.city, resortProfile.state, resortProfile.country].filter(Boolean).join(', ') || 'Resort Stay';
      const emailText = resortProfile.email || 'info@pmsgoresorts.com';
      doc.text(`${locationText} | ${emailText}`, 14, 26);

      // Confirmation badge
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('OFFICIAL BOOKING VOUCHER', 14, 46);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Booking Reference: ${confirmedReservation.bookingId}`, 14, 53);
      doc.text(`Booking Date: ${confirmedReservation.createdAt}`, 14, 59);
      doc.text(`Status: CONFIRMED - ${confirmedReservation.paymentStatus}`, 14, 65);

      // Guest Details Table
      autoTable(doc, {
        startY: 72,
        head: [['Guest Name', 'Contact Phone', 'Email', 'Expected Arrival']],
        body: [[
          `${confirmedReservation.guest.title} ${confirmedReservation.guest.firstName} ${confirmedReservation.guest.lastName}`,
          confirmedReservation.guest.phone,
          confirmedReservation.guest.email,
          `${confirmedReservation.guest.eta || '14:00'} hrs`
        ]],
        theme: 'striped',
        headStyles: { fillColor: [0, 135, 68] }
      });

      // Stay Details Table
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Check-In Date', 'Check-Out Date', 'Duration', 'No. of Guests']],
        body: [[
          confirmedReservation.checkIn,
          confirmedReservation.checkOut,
          `${confirmedReservation.nights} Night(s)`,
          `${adults} Adult(s), ${children} Child(ren)`
        ]],
        theme: 'striped',
        headStyles: { fillColor: [0, 102, 51] }
      });

      // Reserved Rooms Table
      const roomRows = confirmedReservation.rooms.map(r => [
        r.title,
        r.category,
        `${r.quantity} Room(s)`,
        `${currencySymbol} ${((r.basePrice * r.quantity * confirmedReservation.nights)).toLocaleString(currencyLocale)}`
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Room Accommodation', 'Category', 'Quantity', `Amount (${currencyCode})`]],
        body: roomRows,
        foot: confirmedReservation.pricing?.isUae ? [
          ['Room Rate', '', '', `${currencySymbol} ${confirmedReservation.pricing.baseForStay.toLocaleString(currencyLocale)}`],
          ...(confirmedReservation.pricing.discount > 0 ? [['Discount Applied', '', '', `- ${currencySymbol} ${confirmedReservation.pricing.discount.toLocaleString(currencyLocale)}`]] : []),
          ['Service Charge (10%)', '', '', `${currencySymbol} ${confirmedReservation.pricing.serviceCharge.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Municipality Fee (7%)', '', '', `${currencySymbol} ${confirmedReservation.pricing.municipalityFee.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['UAE VAT (5%)', '', '', `${currencySymbol} ${confirmedReservation.pricing.vat.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Tourism Dirham Fee', '', '', `${currencySymbol} ${confirmedReservation.pricing.tourismDirham.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Total Amount Payable', '', '', `${currencySymbol} ${confirmedReservation.pricing.finalTotal.toLocaleString(currencyLocale)}`]
        ] : [
          ['Room Rate', '', '', `${currencySymbol} ${confirmedReservation.pricing.baseForStay.toLocaleString(currencyLocale)}`],
          ['Discount Applied', '', '', `- ${currencySymbol} ${confirmedReservation.pricing.discount.toLocaleString(currencyLocale)}`],
          ['Net Taxable Value', '', '', `${currencySymbol} ${confirmedReservation.pricing.netTaxableValue.toLocaleString(currencyLocale)}`],
          [`Room Tax Amount (${confirmedReservation.pricing.taxLabel || 'GST'})`, '', '', `${currencySymbol} ${confirmedReservation.pricing.totalTaxAmount.toLocaleString(currencyLocale)}`],
          ['Total Amount Payable', '', '', `${currencySymbol} ${confirmedReservation.pricing.finalTotal.toLocaleString(currencyLocale)}`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59] },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
      });

      // Policies & Directions
      const finalY = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Important Instructions & Policies:', 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`• Check-in time: ${resortProfile.checkInTime || '14:00'} | Check-out time: ${resortProfile.checkOutTime || '11:00'}.`, 14, finalY + 6);
      doc.text('• Government-approved photo ID is required for all adult guests at the time of check-in.', 14, finalY + 11);
      if (resortProfile.phone) {
        doc.text(`• Direct resort contact: ${resortProfile.phone} | Reception operates 24/7.`, 14, finalY + 16);
      }
      doc.text(`• GPS Directions: Search "${resortProfile.name || 'Resort'}" on Google Maps.`, 14, finalY + 21);

      doc.save(`${(resortProfile.name || 'Resort').replace(/\s+/g, '_')}_Voucher_${confirmedReservation.bookingId}.pdf`);
      setToast({ open: true, message: 'Booking Voucher PDF downloaded successfully!', severity: 'success' });
    } catch (e) {
      console.error(e);
      window.print();
    }
  };

  if (isLoadingRooms) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', py: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="primary" size={50} />
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Loading resort profile...
            </Typography>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  if (hotelNotFound) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', py: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
              <InfoOutlined sx={{ fontSize: 70, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
                {!urlTenantId ? 'No Hotel Selected' : 'Hotel Unavailable'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {hotelErrorMessage || (!urlTenantId
                  ? 'No hotel or tenant identifier was specified in the URL. Please select a hotel or add tenantId to the URL (e.g. /booking?tenantId=YOUR_TENANT_ID).'
                  : `We couldn't load the resort matching identifier "${urlTenantId}". Please check the booking URL or contact resort administration.`)}
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => window.location.href = 'https://pmsgo.com/location.php'}
              >
                Browse All Resort Locations
              </Button>
            </Paper>
          </Container>
        </Box>

      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pb: 10 }}>

        {/* ─── 1. TOP HEADER BANNER (NO TOP MENU AS REQUESTED) ─── */}
        <Box
          sx={{
            backgroundColor: '#095432',
            color: '#ffffff',
            py: 2.5,
            px: { xs: 2, md: 4 },
            boxShadow: '0 4px 20px rgba(0, 87, 44, 0.2)',
          }}
        >
          <Container maxWidth="xl">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              {/* Resort Branding Title */}
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    fontSize: { xs: '1.25rem', md: '1.65rem' },
                    lineHeight: 1.2
                  }}
                >
                  {resortProfile.name || 'The Crestwood Sanctuary'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    fontSize: '0.85rem'
                  }}
                >
                  <LocationOn sx={{ fontSize: 16 }} />
                  {resortProfile.address ? `${resortProfile.address} ${resortProfile.city ? '(' + resortProfile.city + ')' : ''}` : 'Ernakulam (Vytilla)'} • {resortProfile.tagline || 'Experience Luxury in Vytilla'}
                </Typography>
              </Box>

              {/* Direct Booking Guarantee Badge */}
              <Chip
                icon={<Security sx={{ color: '#ffffff !important', fontSize: 16 }} />}
                label="Official Direct Booking"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: 5,
                  py: 1.8,
                  px: 0.5
                }}
              />
            </Stack>

            {/* ─── 2. DATE & SEARCH BAR (ON TOP BANNER) ─── */}
            <Box
              sx={{
                mt: 2.5,
                p: { xs: 2, md: 2.5 },
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* Check In */}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.75, display: 'block' }}>
                    Check In
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        height: 44,
                        fontSize: '0.875rem'
                      },
                    }}
                  />
                </Grid>

                {/* Check Out */}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.75, display: 'block' }}>
                    Check Out ({numberOfNights} {numberOfNights === 1 ? 'Night' : 'Nights'})
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        height: 44,
                        fontSize: '0.875rem'
                      },
                    }}
                  />
                </Grid>

                {/* Guests & Rooms */}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.75, display: 'block' }}>
                    Guests
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={`${adults}-${children}`}
                    onChange={(e) => {
                      const [a, c] = e.target.value.split('-').map(Number);
                      setAdults(a);
                      setChildren(c);
                    }}
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 2,
                      height: 44,
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'transparent'
                      }
                    }}
                  >
                    <MenuItem value="1-0">1 Adult, 0 Children</MenuItem>
                    <MenuItem value="2-0">2 Adults, 0 Children</MenuItem>
                    <MenuItem value="2-1">2 Adults, 1 Child</MenuItem>
                    <MenuItem value="2-2">2 Adults, 2 Children</MenuItem>
                    <MenuItem value="3-0">3 Adults, 0 Children</MenuItem>
                    <MenuItem value="4-0">4 Adults, 0 Children</MenuItem>
                  </Select>
                </Grid>

                {/* Check Availability Button */}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" sx={{ color: 'transparent', mb: 0.75, display: { xs: 'none', md: 'block' }, userSelect: 'none' }}>
                    &nbsp;
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      fetchLiveAvailability();
                      setToast({
                        open: true,
                        message: `Availability updated for ${numberOfNights} night(s). Best direct rates active!`,
                        severity: 'info'
                      });
                    }}
                    sx={{
                      height: 44,
                      backgroundColor: '#E5A83B',
                      color: '#1a1a1a',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      borderRadius: 2,
                      boxShadow: 'none',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#d99726',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Check Availability
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* ─── 3. SUB-BAR: FILTERS, PRICING TOGGLE & COMPARE ─── */}
        <Box sx={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0', py: 1.5, px: { xs: 2, md: 4 } }}>
          <Container maxWidth="xl">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              {/* Filter Buttons */}
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MenuIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setFilterCategory('ALL')}
                  sx={{
                    borderRadius: '20px',
                    fontSize: '0.82rem',
                    fontWeight: filterCategory === 'ALL' ? 700 : 500,
                    textTransform: 'none',
                    px: 2,
                    py: 0.6,
                    borderColor: filterCategory === 'ALL' ? '#94a3b8' : '#cbd5e1',
                    backgroundColor: filterCategory === 'ALL' ? '#f1f5f9' : '#ffffff',
                    color: filterCategory === 'ALL' ? '#0f172a' : '#475569',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      backgroundColor: '#f8fafc'
                    }
                  }}
                >
                  All Rooms ({totalPhysicalRoomsCount})
                </Button>

                {availableCategories.map((cat) => {
                  const isSelected = filterCategory === cat;
                  const catRooms = roomsList.filter(r => r.category === cat);
                  const catTotalRooms = catRooms.reduce((sum, r) => sum + (r.roomsLeft ?? 1), 0);
                  return (
                    <Button
                      key={cat}
                      size="small"
                      variant="outlined"
                      onClick={() => setFilterCategory(isSelected ? 'ALL' : cat)}
                      sx={{
                        borderRadius: '20px',
                        fontSize: '0.82rem',
                        fontWeight: isSelected ? 700 : 500,
                        textTransform: 'none',
                        px: 2,
                        py: 0.6,
                        borderColor: isSelected ? '#94a3b8' : '#cbd5e1',
                        backgroundColor: isSelected ? '#f1f5f9' : '#ffffff',
                        color: isSelected ? '#0f172a' : '#475569',
                        '&:hover': {
                          borderColor: '#94a3b8',
                          backgroundColor: '#f8fafc'
                        }
                      }}
                    >
                      {cat} ({catTotalRooms})
                    </Button>
                  );
                })}

                {compareList.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<CompareArrows />}
                    onClick={() => setCompareModalOpen(true)}
                    sx={{ borderRadius: '20px', fontWeight: 700, textTransform: 'none' }}
                  >
                    Compare ({compareList.length})
                  </Button>
                )}
              </Stack>

              {/* Price Display Mode */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500, fontSize: '0.85rem' }}>
                  Show Price :
                </Typography>
                <Select
                  size="small"
                  value={priceMode}
                  onChange={(e) => setPriceMode(e.target.value)}
                  sx={{
                    borderRadius: '20px',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    color: '#334155',
                    backgroundColor: '#ffffff',
                    height: 36,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e1'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#94a3b8'
                    },
                    '& .MuiSelect-select': {
                      py: 0.6,
                      px: 2
                    }
                  }}
                >
                  <MenuItem value="wholeStay" sx={{ fontSize: '0.82rem' }}>Price For Whole Stay</MenuItem>
                  <MenuItem value="perNight" sx={{ fontSize: '0.82rem' }}>Per Room Per Night</MenuItem>
                </Select>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ─── 4. MAIN TWO-COLUMN BODY ─── */}
        <Container maxWidth="xl" sx={{ mt: 3 }}>
          <Grid container spacing={3}>

            {/* ─── LEFT COLUMN: ROOM CARDS ─── */}
            <Grid item xs={12} md={8} lg={8}>
              <Stack spacing={2.5}>
                {filteredRooms.map((room) => {
                  const isCompared = compareList.includes(room.id);
                  const isAdded = (selectedRooms[room.id] || 0) > 0;
                  const displayRate = priceMode === 'wholeStay'
                    ? (room.totalStayRate || (room.basePrice * numberOfNights))
                    : (room.perNightRate || room.basePrice);

                  return (
                    <Card
                      key={room.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        transition: 'box-shadow 0.2s',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
                        }
                      }}
                    >
                      {/* Room Photo & Badges */}
                      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 240 }, minHeight: 200, flexShrink: 0 }}>
                        <CardMedia
                          component="img"
                          image={room.image}
                          alt={room.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                            p: 1.5,
                            color: '#ffffff'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.72rem', display: 'block' }}>
                            {room.category || room.title}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Room Info & Details */}
                      <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          {/* Title */}
                          <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, lineHeight: 1.3, fontSize: '1.2rem' }}>
                            {room.title}
                          </Typography>

                          {/* Capacity */}
                          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontSize: '0.85rem' }}>
                              Room Capacity :
                            </Typography>
                            <Person sx={{ fontSize: 16, color: '#1e293b' }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                              {room.capacity}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', ml: 0.5 }}>
                              ({room.adultsBase} Adults, {room.childrenBase} {room.childrenBase === 1 ? 'Child' : 'Children'})
                            </Typography>
                          </Stack>

                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25, fontSize: '0.75rem' }}>
                            Room Rates Inclusive of Tax
                          </Typography>

                          {/* Key Amenities */}
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.8 }}>
                            {room.amenities.map((am, i) => (
                              <Chip
                                key={i}
                                label={am}
                                size="small"
                                sx={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '16px',
                                  fontSize: '0.72rem',
                                  color: '#475569',
                                  height: 26,
                                  fontWeight: 500
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>

                        {/* Bottom Row: Actions & Price */}
                        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            flexWrap="wrap"
                            spacing={1.5}
                          >
                            {/* Left: Info Links & Compare */}
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                              <Button
                                size="small"
                                onClick={() => setActiveInfoRoom(room)}
                                sx={{ color: '#0c5834', p: 0, minWidth: 'auto', fontSize: '0.85rem', fontWeight: 700, textTransform: 'none' }}
                              >
                                Room Info
                              </Button>
                              <Typography variant="caption" sx={{ color: '#cbd5e1' }}>•</Typography>
                              <Button
                                size="small"
                                onClick={() => handleOpenEnquiry(room)}
                                sx={{ color: '#0c5834', p: 0, minWidth: 'auto', fontSize: '0.85rem', fontWeight: 700, textTransform: 'none' }}
                              >
                                Enquire
                              </Button>

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={isCompared}
                                    onChange={() => handleToggleCompare(room.id)}
                                    sx={{ p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#0c5834' } }}
                                  />
                                }
                                label={<Typography variant="caption" sx={{ color: '#475569', fontSize: '0.78rem' }}>Add To Compare</Typography>}
                                sx={{ ml: 1, mr: 0 }}
                              />
                            </Stack>

                            {/* Right: Price & Add Room Button */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 800, lineHeight: 1.1, fontSize: '1.25rem' }}>
                                  {currencySymbol} {displayRate.toLocaleString(currencyLocale)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                                  {priceMode === 'wholeStay'
                                    ? `Price for ${numberOfNights} Night(s)`
                                    : 'Price for 1 Night(s)'}
                                </Typography>
                              </Box>

                              {isAdded ? (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{
                                    border: '1px solid #0c5834',
                                    borderRadius: '20px',
                                    px: 1,
                                    py: 0.3,
                                    backgroundColor: '#ffffff'
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateQuantity(room.id, -1)}
                                    sx={{ color: '#0c5834', p: 0.4 }}
                                  >
                                    <Remove sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <Typography variant="body2" sx={{ px: 1, fontWeight: 700, color: '#1e293b' }}>
                                    {selectedRooms[room.id]}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    disabled={selectedRooms[room.id] >= room.roomsLeft}
                                    onClick={() => handleUpdateQuantity(room.id, 1)}
                                    sx={{ color: selectedRooms[room.id] >= room.roomsLeft ? '#94a3b8' : '#0c5834', p: 0.4 }}
                                  >
                                    <Add sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Stack>
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={room.roomsLeft <= 0}
                                  onClick={() => handleAddRoom(room.id)}
                                  sx={{
                                    backgroundColor: room.roomsLeft <= 0 ? '#e2e8f0' : '#0c5834',
                                    color: room.roomsLeft <= 0 ? '#64748b' : '#ffffff',
                                    fontWeight: 700,
                                    px: 2.8,
                                    py: 0.8,
                                    whiteSpace: 'nowrap',
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem',
                                    boxShadow: 'none',
                                    '&:hover': {
                                      backgroundColor: room.roomsLeft <= 0 ? '#e2e8f0' : '#084227',
                                      boxShadow: 'none'
                                    }
                                  }}
                                >
                                  {room.roomsLeft <= 0 ? 'Sold Out' : 'Add Room'}
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Grid>

            {/* ─── RIGHT COLUMN: STICKY BOOKING SUMMARY (MATCHES SCREENSHOT) ─── */}
            <Grid item xs={12} md={4} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  position: 'sticky',
                  top: 24,
                  borderRadius: 4,
                  border: '1px solid #e2e8f0',
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  p: 3
                }}
              >
                {/* Header */}
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2.5, fontSize: '1.2rem' }}>
                  Booking Summary
                </Typography>

                {/* Content */}
                {totalSelectedRoomsCount === 0 ? (
                  // Empty State matching screenshot
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <KingBed sx={{ fontSize: 64, color: '#cbd5e1', mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#64748b' }}>
                      No Room(s) Selected
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1, px: 2 }}>
                      Click &ldquo;Add Room&rdquo; on any room on the left to start your reservation.
                    </Typography>
                  </Box>
                ) : (
                  // Populated Summary State
                  <Box>
                    {/* Stay Duration Details */}
                    <Box sx={{ pb: 2, mb: 2, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.72rem' }}>
                        STAY DETAILS
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#1e293b', mt: 0.5, fontSize: '0.95rem' }}>
                        {checkIn} → {checkOut} ({numberOfNights} {numberOfNights === 1 ? 'Night' : 'Nights'})
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.25, display: 'block' }}>
                        {adults} Adults, {children} Children • {totalSelectedRoomsCount} Room(s)
                      </Typography>
                    </Box>

                    {/* Selected Room Items */}
                    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                      {Object.entries(selectedRooms).map(([roomId, qty]) => {
                        const room = roomsList.find(r => r.id === roomId);
                        if (!room) return null;
                        const roomTotal = room.basePrice * qty * numberOfNights;

                        return (
                          <Box
                            key={roomId}
                            sx={{
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 2.5,
                              border: '1px solid #f1f5f9'
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                {room.title}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveRoom(roomId)}
                                sx={{ color: '#94a3b8', p: 0.2, '&:hover': { color: '#ef4444' } }}
                              >
                                <DeleteOutline sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#0c5834', fontWeight: 700, fontSize: '0.8rem' }}>
                                Qty: {qty}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0c5834', fontSize: '0.85rem' }}>
                                {currencySymbol} {roomTotal.toLocaleString(currencyLocale)}
                              </Typography>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>

                    {/* Discount / Promo Code Input Section */}
                    <Box sx={{ mb: 2.5, pt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, fontSize: '0.85rem' }}>
                        Apply Discount / Promo Code
                      </Typography>

                      {appliedPromo ? (
                        <Box
                          sx={{
                            p: 1.2,
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalOffer sx={{ color: '#0c5834', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0c5834', display: 'block' }}>
                                {appliedPromo.code} ({appliedPromo.discountPercent}% OFF)
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#15803d', fontSize: '0.7rem' }}>
                                Saved {currencySymbol} {pricingBreakdown.discount.toLocaleString(currencyLocale)}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedPromo(null);
                              setPromoCodeInput('');
                              setToast({ open: true, message: 'Discount removed', severity: 'info' });
                            }}
                            sx={{ color: '#ef4444', p: 0.5 }}
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <TextField
                            placeholder="Enter Promo or % (e.g. 10%)"
                            size="small"
                            value={promoCodeInput}
                            onChange={(e) => setPromoCodeInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleApplyPromoCode();
                            }}
                            sx={{
                              flexGrow: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: '#ffffff',
                                height: 40,
                                fontSize: '0.85rem'
                              }
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyPromoCode}
                            sx={{
                              backgroundColor: '#0f172a',
                              color: '#ffffff',
                              fontWeight: 700,
                              borderRadius: 2,
                              px: 2.5,
                              height: 40,
                              whiteSpace: 'nowrap',
                              textTransform: 'none',
                              boxShadow: 'none',
                              '&:hover': { backgroundColor: '#1e293b', boxShadow: 'none' }
                            }}
                          >
                            Apply
                          </Button>
                        </Stack>
                      )}
                    </Box>

                    {/* Price Breakdown */}
                    <Stack spacing={1.2} sx={{ pt: 1, mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                          Room Rate
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                          {currencySymbol} {pricingBreakdown.baseForStay.toLocaleString(currencyLocale)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                          Discount Applied
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: pricingBreakdown.discount > 0 ? '#0c5834' : '#1e293b', fontSize: '0.85rem' }}>
                          {pricingBreakdown.discount > 0 ? `- ${currencySymbol} ${pricingBreakdown.discount.toLocaleString(currencyLocale)}` : `${currencySymbol} 0`}
                        </Typography>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                          Net Taxable Value
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                          {currencySymbol} {pricingBreakdown.netTaxableValue.toLocaleString(currencyLocale)}
                        </Typography>
                      </Stack>

                      {pricingBreakdown.isUae ? (
                        <>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                              Service Charge (10%)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                              {currencySymbol} {pricingBreakdown.serviceCharge.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                              Municipality Fee (7%)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                              {currencySymbol} {pricingBreakdown.municipalityFee.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                              UAE VAT (5%)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                              {currencySymbol} {pricingBreakdown.vat.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                              Tourism Dirham Fee
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                              {currencySymbol} {pricingBreakdown.tourismDirham.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Stack>
                        </>
                      ) : (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                            Room Tax Amount
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.85rem' }}>
                            {currencySymbol} {pricingBreakdown.totalTaxAmount.toLocaleString(currencyLocale)} ({pricingBreakdown.taxLabel})
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Divider sx={{ my: 1.5, borderColor: '#f1f5f9' }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ my: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Total Payable
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0c5834', fontSize: '1.45rem' }}>
                        {currencySymbol} {pricingBreakdown.finalTotal.toLocaleString(currencyLocale)}
                      </Typography>
                    </Stack>

                    {/* PRIMARY ACTION: PROCEED TO BOOK */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => setCheckoutModalOpen(true)}
                      sx={{
                        backgroundColor: '#0c5834',
                        color: '#ffffff',
                        fontWeight: 700,
                        py: 1.4,
                        fontSize: '0.95rem',
                        borderRadius: 3,
                        boxShadow: 'none',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#084227',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      Proceed to Book
                    </Button>

                    {/* Direct Booking Assurance */}
                    <Box
                      sx={{
                        mt: 2,
                        py: 1,
                        px: 2,
                        backgroundColor: '#ecfdf5',
                        borderRadius: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75
                      }}
                    >
                      <Done sx={{ fontSize: 16, color: '#065f46', fontWeight: 800 }} />
                      <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 600, fontSize: '0.75rem' }}>
                        Best Rate Guaranteed • Instant Confirmation
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

          </Grid>
        </Container>

        {/* ─── 5. BOOKING & CHECKOUT DIALOG ("able to book from it") ─── */}
        <Dialog
          open={checkoutModalOpen}
          onClose={handleCloseCheckout}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          {bookingConfirmed ? (
            // ── SUCCESS CONFIRMATION SCREEN ──
            <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
              <IconButton
                onClick={handleCloseCheckout}
                sx={{ position: 'absolute', top: 12, right: 12, color: '#64748b' }}
                size="small"
              >
                <Close />
              </IconButton>
              <CheckCircle sx={{ fontSize: 72, color: '#008744', mb: 1.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                Booking Confirmed Successfully!
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', mt: 0.5 }}>
                Thank you, {confirmedReservation?.guest.firstName}. Your reservation at {resortProfile.name || 'the resort'} is secured.
              </Typography>

              {/* Booking Reference Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mt: 3,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2.5,
                  textAlign: 'left'
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      BOOKING REFERENCE ID
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#008744' }}>
                      {confirmedReservation?.bookingId}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      PAYMENT STATUS
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {confirmedReservation?.paymentStatus}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Check-In Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {confirmedReservation?.checkIn} (from 2:00 PM)
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Check-Out Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {confirmedReservation?.checkOut} (until 11:00 AM)
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#008744' }}>
                      {currencySymbol} {confirmedReservation?.pricing.finalTotal.toLocaleString(currencyLocale)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {confirmedReservation?.pricing?.isUae && (
                <Alert severity="info" sx={{ mt: 2.5, textAlign: 'left', borderRadius: 2, border: '1px solid #bae6fd', bgcolor: '#f0f9ff' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0369a1' }}>
                    Government Clearance: Pending
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#0c4a6e', display: 'block', mt: 0.5 }}>
                    In accordance with Dubai / UAE Tourism (DET) and local hospitality regulations, your reservation is currently undergoing mandatory government verification. You will receive an official confirmation voucher once clearance is completed.
                  </Typography>
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3.5 }}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={handleDownloadVoucherPdf}
                  sx={{
                    backgroundColor: '#008744',
                    color: '#ffffff',
                    fontWeight: 700,
                    px: 3,
                    py: 1.2,
                    borderRadius: 2,
                    '&:hover': { backgroundColor: '#006633' }
                  }}
                >
                  Download PDF Voucher
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={() => window.print()}
                  sx={{ borderRadius: 2, borderColor: '#cbd5e1', color: '#475569' }}
                >
                  Print Summary
                </Button>

                <Button
                  variant="text"
                  onClick={handleCloseCheckout}
                  sx={{ color: '#008744', fontWeight: 600 }}
                >
                  Book Another Stay
                </Button>
              </Stack>
            </Box>
          ) : (
            // ── GUEST DETAILS & PAYMENT CHECKOUT FORM ──
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                    Complete Your Reservation
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {resortProfile.name || 'Resort'} • Direct Guest Booking
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseCheckout} size="small">
                  <Close />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers sx={{ py: 3 }}>
                <Grid container spacing={3}>
                  {/* Left Column: Form Fields */}
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#008744', mb: 2 }}>
                      1. Primary Guest Contact Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Title"
                          value={guestDetails.title}
                          onChange={(e) => setGuestDetails({ ...guestDetails, title: e.target.value })}
                        >
                          <MenuItem value="Mr">Mr.</MenuItem>
                          <MenuItem value="Ms">Ms.</MenuItem>
                          <MenuItem value="Mrs">Mrs.</MenuItem>
                          <MenuItem value="Dr">Dr.</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={4.5}>
                        <TextField
                          required
                          fullWidth
                          size="small"
                          label="First Name"
                          value={guestDetails.firstName}
                          onChange={(e) => setGuestDetails({ ...guestDetails, firstName: e.target.value })}
                        />
                      </Grid>

                      <Grid item xs={4.5}>
                        <TextField
                          required
                          fullWidth
                          size="small"
                          label="Last Name"
                          value={guestDetails.lastName}
                          onChange={(e) => setGuestDetails({ ...guestDetails, lastName: e.target.value })}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          type="email"
                          size="small"
                          label="Email Address"
                          helperText="Voucher will be emailed here"
                          value={guestDetails.email}
                          onChange={(e) => setGuestDetails({ ...guestDetails, email: e.target.value })}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          size="small"
                          label="Mobile Phone Number"
                          placeholder="+91 98765 43210"
                          helperText="For SMS & WhatsApp updates"
                          value={guestDetails.phone}
                          onChange={(e) => setGuestDetails({ ...guestDetails, phone: e.target.value })}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City / State"
                          value={guestDetails.city}
                          onChange={(e) => setGuestDetails({ ...guestDetails, city: e.target.value })}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Estimated Arrival Time"
                          value={guestDetails.eta}
                          onChange={(e) => setGuestDetails({ ...guestDetails, eta: e.target.value })}
                        >
                          <MenuItem value="12:00">12:00 PM (Early Request)</MenuItem>
                          <MenuItem value="14:00">02:00 PM (Standard Check-in)</MenuItem>
                          <MenuItem value="16:00">04:00 PM</MenuItem>
                          <MenuItem value="18:00">06:00 PM</MenuItem>
                          <MenuItem value="20:00">08:00 PM or Later</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          label="Special Requests / Dietary Requirements"
                          placeholder="e.g. Honeymoon setup, extra cot, high floor, quiet zone"
                          value={guestDetails.specialRequests}
                          onChange={(e) => setGuestDetails({ ...guestDetails, specialRequests: e.target.value })}
                        />
                      </Grid>
                    </Grid>

                    {/* Payment Options */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#008744', mt: 3, mb: 1.5 }}>
                      2. Payment Preference
                    </Typography>

                    <FormControl component="fieldset">
                      <RadioGroup
                        value={guestDetails.paymentMethod}
                        onChange={(e) => setGuestDetails({ ...guestDetails, paymentMethod: e.target.value })}
                      >
                        <FormControlLabel
                          value="payAtHotel"
                          control={<Radio sx={{ '&.Mui-checked': { color: '#008744' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Pay at Resort on Check-In
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                No advance required now. Settle at front desk via Cash, UPI, or Card.
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1 }}
                        />

                        <FormControlLabel
                          value="online"
                          control={<Radio sx={{ '&.Mui-checked': { color: '#008744' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Pay Online (Instant UPI / Cards)
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                100% Secure encrypted transaction with guaranteed room hold.
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={guestDetails.termsAccepted}
                            onChange={(e) => setGuestDetails({ ...guestDetails, termsAccepted: e.target.checked })}
                            sx={{ '&.Mui-checked': { color: '#008744' } }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            I agree to the Resort Booking Policies, Free Cancellation up to 48 hrs, and Check-in Terms.
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>

                  {/* Right Column: Order Recap */}
                  <Grid item xs={12} md={5}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        backgroundColor: '#f8fafc',
                        borderRadius: 2.5,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
                        Reservation Summary
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        Dates: {checkIn} to {checkOut}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                        {numberOfNights} Night(s) • {adults} Adults
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={1}>
                        {Object.entries(selectedRooms).map(([id, qty]) => {
                          const rm = roomsList.find(r => r.id === id);
                          if (!rm) return null;
                          return (
                            <Stack key={id} direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 600 }}>
                                {qty}x {rm.title}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {currencySymbol} {(rm.basePrice * qty * numberOfNights).toLocaleString(currencyLocale)}
                              </Typography>
                            </Stack>
                          );
                        })}

                        <Divider sx={{ my: 0.5 }} />

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Room Rate
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {currencySymbol} {pricingBreakdown.baseForStay.toLocaleString(currencyLocale)}
                          </Typography>
                        </Stack>

                        {appliedPromo && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" sx={{ color: '#008744', fontWeight: 600 }}>
                              Discount Applied ({appliedPromo.code})
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#008744' }}>
                              - {currencySymbol} {pricingBreakdown.discount.toLocaleString(currencyLocale)}
                            </Typography>
                          </Stack>
                        )}

                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Net Taxable Value
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {currencySymbol} {pricingBreakdown.netTaxableValue.toLocaleString(currencyLocale)}
                          </Typography>
                        </Stack>

                        {pricingBreakdown.isUae ? (
                          <>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Service Charge (10%)
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {currencySymbol} {pricingBreakdown.serviceCharge.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Municipality Fee (7%)
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {currencySymbol} {pricingBreakdown.municipalityFee.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                UAE VAT (5%)
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {currencySymbol} {pricingBreakdown.vat.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Tourism Dirham Fee
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {currencySymbol} {pricingBreakdown.tourismDirham.toLocaleString(currencyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Stack>
                          </>
                        ) : (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              Room Tax Amount
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb' }}>
                              {currencySymbol} {pricingBreakdown.totalTaxAmount.toLocaleString(currencyLocale)} ({pricingBreakdown.taxLabel})
                            </Typography>
                          </Stack>
                        )}

                        <Divider sx={{ my: 1 }} />

                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            Total Amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#008744' }}>
                            {currencySymbol} {pricingBreakdown.finalTotal.toLocaleString(currencyLocale)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc' }}>
                <Button
                  onClick={handleCloseCheckout}
                  disabled={isSubmittingBooking}
                  sx={{ color: '#64748b', fontWeight: 600 }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmitBooking}
                  disabled={isSubmittingBooking || !guestDetails.termsAccepted}
                  sx={{
                    backgroundColor: '#008744',
                    color: '#ffffff',
                    fontWeight: 700,
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    '&:hover': { backgroundColor: '#006633' }
                  }}
                >
                  {isSubmittingBooking ? 'Securing Booking...' : 'Confirm & Reserve Now'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ─── 6. ROOM INFO DIALOG ─── */}
        <Dialog
          open={Boolean(activeInfoRoom)}
          onClose={() => setActiveInfoRoom(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {activeInfoRoom && (
            <>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="220"
                  image={activeInfoRoom.image}
                  alt={activeInfoRoom.title}
                />
                <IconButton
                  onClick={() => setActiveInfoRoom(null)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Box>

              <DialogContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#008744' }}>
                  {activeInfoRoom.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, mb: 2 }}>
                  {activeInfoRoom.plan} • {activeInfoRoom.size}
                </Typography>

                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, mb: 2.5 }}>
                  {activeInfoRoom.description}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                  Room Features & Amenities:
                </Typography>
                <Grid container spacing={1}>
                  {activeInfoRoom.amenities.map((am, i) => (
                    <Grid item xs={6} key={i}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircle sx={{ fontSize: 16, color: '#008744' }} />
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {am}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  • Bed Type: {activeInfoRoom.bedType}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  • Max Guests: {activeInfoRoom.capacity} Guests
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  • Cancellation: Free cancellation up to 48 hours prior to check-in.
                </Typography>
              </DialogContent>

              <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                <Button onClick={() => setActiveInfoRoom(null)} sx={{ color: '#64748b' }}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    const roomToEnquire = activeInfoRoom;
                    setActiveInfoRoom(null);
                    handleOpenEnquiry(roomToEnquire);
                  }}
                  sx={{ color: '#008744', fontWeight: 600, mr: 1 }}
                >
                  Enquire
                </Button>
                <Button
                  variant="contained"
                  disabled={activeInfoRoom.roomsLeft <= 0}
                  onClick={() => {
                    handleAddRoom(activeInfoRoom.id);
                    setActiveInfoRoom(null);
                  }}
                  sx={{
                    backgroundColor: activeInfoRoom.roomsLeft <= 0 ? '#e2e8f0' : '#008744',
                    color: activeInfoRoom.roomsLeft <= 0 ? '#64748b' : '#fff',
                    fontWeight: 700,
                    cursor: activeInfoRoom.roomsLeft <= 0 ? 'not-allowed' : 'pointer',
                    '&.Mui-disabled': {
                      backgroundColor: '#e2e8f0',
                      color: '#64748b'
                    }
                  }}
                >
                  {activeInfoRoom.roomsLeft <= 0
                    ? 'Sold Out'
                    : `Add This Room (${currencySymbol} ${activeInfoRoom.basePrice.toLocaleString(currencyLocale)})`}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ─── 6.5. ROOM ENQUIRY MODAL ─── */}
        <Dialog
          open={enquiryModalOpen}
          onClose={handleCloseEnquiry}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#008744', fontSize: '1.2rem' }}>
                Enquire About Stay
              </Typography>
              {enquiryRoom && (
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {enquiryRoom.title}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseEnquiry} size="small">
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {enquiryResult ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ fontSize: 60, color: '#008744', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                  Enquiry Submitted!
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                  {enquiryResult.confirmationMessage || 'Thank you for your enquiry! Our reservation desk will reach out to you shortly.'}
                </Typography>
                {enquiryResult.enquiryId && (
                  <Paper sx={{ p: 2, backgroundColor: '#f8fafc', border: '1px dashed #008744', borderRadius: 2, display: 'inline-block', mb: 3 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Enquiry Reference ID
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#008744' }}>
                      {enquiryResult.enquiryId}
                    </Typography>
                  </Paper>
                )}
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleCloseEnquiry}
                    sx={{ backgroundColor: '#008744', color: '#fff', fontWeight: 700, px: 4 }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            ) : (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {enquiryRoom && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {enquiryRoom.image && (
                        <Box
                          component="img"
                          src={enquiryRoom.image}
                          alt={enquiryRoom.title}
                          sx={{ width: 70, height: 55, borderRadius: 1.5, objectFit: 'cover' }}
                        />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {enquiryRoom.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#008744', fontWeight: 700 }}>
                          {currencySymbol} {enquiryRoom.basePrice?.toLocaleString(currencyLocale)} / Night
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Full Name *"
                      fullWidth
                      size="small"
                      value={enquiryForm.name}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your full name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address *"
                      type="email"
                      fullWidth
                      size="small"
                      value={enquiryForm.email}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="guest@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number *"
                      type="tel"
                      fullWidth
                      size="small"
                      value={enquiryForm.phone}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 9876543210"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Check-In Date"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={enquiryForm.checkIn}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, checkIn: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Check-Out Date"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={enquiryForm.checkOut}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, checkOut: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Enquiry Message / Special Requests *"
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={enquiryForm.message}
                      onChange={(e) => setEnquiryForm((prev) => ({ ...prev, message: e.target.value }))}
                      placeholder="Mention any specific requirements, extra bed needs, group sizes, or questions..."
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}
          </DialogContent>

          {!enquiryResult && (
            <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
              <Button onClick={handleCloseEnquiry} sx={{ color: '#64748b' }} disabled={isSubmittingEnquiry}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitEnquiry}
                disabled={isSubmittingEnquiry}
                sx={{ backgroundColor: '#008744', color: '#fff', fontWeight: 700, px: 3, '&:hover': { backgroundColor: '#006633' } }}
              >
                {isSubmittingEnquiry ? <CircularProgress size={20} color="inherit" /> : 'Submit Enquiry'}
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* ─── 7. COMPARE ROOMS MODAL ─── */}
        <Dialog
          open={compareModalOpen}
          onClose={() => setCompareModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Compare Rooms
            </Typography>
            <IconButton onClick={() => setCompareModalOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                    {compareList.map(id => {
                      const r = roomsList.find(x => x.id === id);
                      return (
                        <TableCell key={id} sx={{ fontWeight: 800, color: '#008744' }}>
                          {r?.title}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Rate / Night</TableCell>
                    {compareList.map(id => {
                      const r = roomsList.find(x => x.id === id);
                      return (
                        <TableCell key={id} sx={{ fontWeight: 700 }}>
                          {currencySymbol} {r?.basePrice.toLocaleString(currencyLocale)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Max Capacity</TableCell>
                    {compareList.map(id => {
                      const r = roomsList.find(x => x.id === id);
                      return <TableCell key={id}>{r?.capacity} Guests</TableCell>;
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Room Size</TableCell>
                    {compareList.map(id => {
                      const r = roomsList.find(x => x.id === id);
                      return <TableCell key={id}>{r?.size}</TableCell>;
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    {compareList.map(id => (
                      <TableCell key={id}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            handleAddRoom(id);
                            setCompareModalOpen(false);
                          }}
                          sx={{ backgroundColor: '#008744', color: '#fff', fontSize: '0.75rem' }}
                        >
                          Add to Book
                        </Button>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>

        {/* ─── 8. PROMO CODE MODAL ─── */}
        <Dialog
          open={promoDialogOpen}
          onClose={() => setPromoDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Apply Promotional Code</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Enter your promotional or corporate code for exclusive direct booking discounts.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Promo Code"
              placeholder="e.g. PMSGO10"
              value={promoCodeInput}
              onChange={(e) => setPromoCodeInput(e.target.value)}
            />
            <Typography variant="caption" sx={{ color: '#008744', fontWeight: 600, display: 'block', mt: 1 }}>
              Tip: Use &ldquo;PMSGO10&rdquo; for 10% instant discount!
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPromoDialogOpen(false)} sx={{ color: '#64748b' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyPromoCode}
              sx={{ backgroundColor: '#008744', color: '#fff', fontWeight: 700 }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* ─── 9. BOTTOM COOKIE BANNER (MATCHING SCREENSHOT) ─── */}
        {cookieConsent && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              borderTop: '1px solid #e2e8f0',
              py: 1.5,
              px: 3,
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Typography variant="body2" sx={{ color: '#475569' }}>
              We use cookies to give you a better experience on our website.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Done sx={{ fontSize: 16 }} />}
              onClick={() => setCookieConsent(false)}
              sx={{
                backgroundColor: '#1e293b',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#0f172a' }
              }}
            >
              Got it
            </Button>
          </Box>
        )}

        {/* Simulated Razorpay Test Checkout Modal (Dev/Test Mode) */}
        <Dialog
          open={testRazorpayModal.open}
          onClose={() => setTestRazorpayModal({ open: false, orderData: null, bookedRoomsData: null })}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, p: 1 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ bgcolor: '#072654', color: '#fff', px: 1.5, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 700 }}>
                RAZORPAY
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Checkout
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setTestRazorpayModal({ open: false, orderData: null, bookedRoomsData: null })}>
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Order ID: <strong>{testRazorpayModal.orderData?.orderId}</strong>
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#008744', my: 1.5 }}>
                {currencySymbol} {testRazorpayModal.orderData?.amount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                Booking Reference: {testRazorpayModal.orderData?.bookingReference}
              </Typography>
              <Alert severity="info" sx={{ textAlign: 'left', fontSize: 12, mb: 2 }}>
                Test Mode: Select simulated payment result below to complete booking verification.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{ bgcolor: '#008744', '&:hover': { bgcolor: '#006b35' }, py: 1.2, fontWeight: 700 }}
              onClick={async () => {
                const orderData = testRazorpayModal.orderData;
                const bookedRoomsData = testRazorpayModal.bookedRoomsData;
                setTestRazorpayModal({ open: false, orderData: null, bookedRoomsData: null });
                try {
                  const verifyRes = await BookingEngineService.verifyRazorpayPayment({
                    tenantId: urlTenantId,
                    bookingReference: orderData.bookingReference,
                    razorpayOrderId: orderData.orderId,
                    razorpayPaymentId: `pay_test_${Math.random().toString(36).substring(2, 10)}`,
                    razorpaySignature: 'test_signature'
                  });

                  if (verifyRes?.status === 'success') {
                    const confirmedData = {
                      bookingId: orderData.bookingReference,
                      guest: guestDetails,
                      rooms: bookedRoomsData,
                      checkIn,
                      checkOut,
                      nights: numberOfNights,
                      pricing: pricingBreakdown,
                      paymentStatus: 'Paid Online (Guaranteed)',
                      createdAt: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                    };
                    setConfirmedReservation(confirmedData);
                    setBookingConfirmed(true);
                    fetchLiveAvailability();
                  } else {
                    setToast({ open: true, message: 'Payment verification failed.', severity: 'error' });
                  }
                } catch (err) {
                  setToast({ open: true, message: 'Error verifying Razorpay payment.', severity: 'error' });
                }
              }}
            >
              Simulate Pay Online (Success)
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => {
                setTestRazorpayModal({ open: false, orderData: null, bookedRoomsData: null });
                setToast({ open: true, message: 'Online payment was cancelled by guest.', severity: 'warning' });
              }}
            >
              Cancel Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Snackbar */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
            {toast.message}
          </Alert>
        </Snackbar>

      </Box>
    </ThemeProvider>
  );
}
